// @ts-nocheck
import React, { useMemo } from 'react';
import ReactTooltip from 'react-tooltip';

import cn from 'class-names';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import { billingAppCreditsSelector } from '@redux/selectors/billings-app.selectors';
import { toggleNotificationsSidebar } from '@redux/actions/appState.actions';
import { appStateSelector } from '@redux/selectors/appState.selectors';

import urls from '@constants/urls';

import userLinksConfig from '@components/UserLinks/userLinksConfig';

import './UserLinks.scss';
import { redirectUserTo } from '@utils/historyHandler';
import { isAppRealm, isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import { billingConciergeDetailsSelector } from '@redux/selectors/billings-concierge.selectors';
import { isSemrushMode, isStandaloneMode } from '@constants/app-modes';
import { billingPublisherDetailsSelector } from '@redux/selectors/billings-publisher.selectors';

/* Test ids for ui-automation-tests; the account link is the way into settings */
const USER_LINK_TEST_IDS = {
  account: 'sidebar-settings',
  billing: 'sidebar-billing',
  notifications: 'sidebar-notifications',
};

function UserLinks(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { data: appCredits } = useSelector(billingAppCreditsSelector);
  const { data: publisherBilling } = useSelector(billingPublisherDetailsSelector);
  const { data: conciergeBilling } = useSelector(billingConciergeDetailsSelector);
  const { isPaymentStatusActive } = useSelector(appStateSelector);

  const handleOpenNotificationSidebar = (evt) => {
    evt.stopPropagation();
    dispatch(toggleNotificationsSidebar(true));
  };

  const handlerMapper = useMemo(
    () => ({
      notifications: handleOpenNotificationSidebar,
      account: (e) => {
        e.stopPropagation();
        if (isStandaloneMode()) {
          redirectUserTo(history, urls.PROFILE);
        } else {
          redirectUserTo(history, urls.SETTINGS_WORKSPACE);
        }
      },
      billing: (e) => {
        e.stopPropagation();
        redirectUserTo(history, urls.SETTINGS_BILLING);
        console.log('Redirecting to billing from user links component');
      },
    }),
    [history]
  );

  const billingProps = useMemo(() => {
    if (isConciergeRealm()) {
      return { credits: conciergeBilling };
    }
    if (isPublisherRealm()) {
      return { credits: publisherBilling };
    }

    return { credits: appCredits };
  }, [appCredits, conciergeBilling, publisherBilling]);

  const propsMapper = useMemo(() => ({ billing: billingProps }), [billingProps]);

  const isBlocked = isAppRealm() && !isPaymentStatusActive && !isSemrushMode();

  const cnUserLink = cn('user-link', {
    'user-link--short': true,
    'user-link--blocked': isBlocked,
  });

  const cnUserLinkWrapper = cn('user-links-wrapper', {
    'user-links-wrapper--blocked': isBlocked,
  });

  return (
    <div className={cnUserLinkWrapper}>
      {userLinksConfig.map(({ key, icon, positionElements, Children, Tooltip }) => {
        return (
          <div
            key={key}
            className={`${cnUserLink} user-link--${positionElements}`}
            onClick={!isBlocked ? handlerMapper[key] : undefined}
            tabIndex={0}
            role="button"
            data-for={`user-link-${key}`}
            data-tip=""
            data-testid={USER_LINK_TEST_IDS[key]}
          >
            <div className="user-link__icon">
              <SVGIcon icon={icon} color="#BDBDBD" />
            </div>

            <Children renderProps={propsMapper[key]} />

            {Tooltip && (
              <ReactTooltip
                id={`user-link-${key}`}
                className="react-tooltip"
                place="right"
                effect="solid"
                offset={{ right: 20, top: 0 }}
                arrowColor="transparent"
              >
                <div className="user-link__tooltip-content">
                  <Tooltip renderProps={propsMapper[key]} />
                </div>
              </ReactTooltip>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default UserLinks;
